import { apiUrl } from "../utils/api";
import type { VisitenkarteBefund } from "../utils/visitenkarteZuFormState";
import type { PersonalausweisBefund } from "../utils/personalausweisZuFormState";
import type { GehaltsabrechnungBefund } from "../utils/gehaltsabrechnungZuFormState";
import type {
  FuehrerscheinBefund,
  GeburtsurkundeBefund,
  HeiratsurkundeBefund,
  SterbeurkundeBefund,
} from "../utils/urkundenZuFormState";

/**
 * Ruft das Auslesen eines hochgeladenen Dokuments auf
 * (`POST /api/ocr/{visitenkarte|personalausweis|gehaltsabrechnung}`).
 *
 * Je Aufnahme ein eigener Aufruf: der vorgelagerte nginx begrenzt den gesamten
 * /api/-Block auf 12 MB, und bei zwei Fotos in einem Request zählt die Summe.
 * Ein 413 käme von dort als HTML-Seite zurück, die die Oberfläche nicht als
 * Fehler darstellen kann.
 *
 * Die vierte Dokumentart — die vCard — läuft NICHT über diesen Weg: eine .vcf
 * ist bereits strukturiert und wird in `utils/vcardLesen` im Browser gelesen,
 * ohne Server und ohne dass die Datei den Rechner verlässt.
 */

export type ScanZiel =
  | "visitenkarte"
  | "personalausweis"
  | "gehaltsabrechnung"
  | "fuehrerschein"
  | "geburtsurkunde"
  | "heiratsurkunde"
  | "sterbeurkunde";

export type ScanAntwort<T> = {
  name: string;
  felder: T;
  roh?: { text: string; modell: string; dauerMs: number };
};

/** Bilder, die der Browser selbst nicht anzeigen kann (iPhone-Standardformat). */
const OHNE_VORSCHAU = /\.(heic|heif)$/i;

export const hatVorschau = (datei: File): boolean =>
  !OHNE_VORSCHAU.test(datei.name) &&
  !/^image\/(heic|heif)/i.test(datei.type) &&
  datei.type !== "application/pdf";

export class DokumentScanFehler extends Error {}

const BEZEICHNUNG: Record<ScanZiel, string> = {
  visitenkarte: "Die Visitenkarte",
  personalausweis: "Der Ausweis",
  gehaltsabrechnung: "Die Gehaltsabrechnung",
  fuehrerschein: "Der Führerschein",
  geburtsurkunde: "Die Geburtsurkunde",
  heiratsurkunde: "Die Heiratsurkunde",
  sterbeurkunde: "Die Sterbeurkunde",
};

const leseDokument = async <T>(ziel: ScanZiel, datei: File): Promise<ScanAntwort<T>> => {
  const daten = new FormData();
  daten.append("file", datei);
  const was = BEZEICHNUNG[ziel];

  let antwort: Response;
  try {
    // Kein `credentials`: die Anmeldung läuft über den Bearer-Token, den der
    // in `utils/api.ts` gepatchte `window.fetch` an jeden API-Aufruf hängt.
    // Cookies werden dafür ausdrücklich nicht mehr benutzt.
    antwort = await fetch(apiUrl(`ocr/${ziel}`), { method: "POST", body: daten });
  } catch {
    throw new DokumentScanFehler(
      "Keine Verbindung zum Server. Bitte die Felder von Hand ausfüllen.",
    );
  }

  if (antwort.status === 413) {
    throw new DokumentScanFehler("Die Datei ist zu groß. Bitte eine kleinere Aufnahme wählen.");
  }
  if (antwort.status === 503) {
    throw new DokumentScanFehler(
      "Die Texterkennung ist gerade nicht erreichbar. Bitte die Felder von Hand ausfüllen.",
    );
  }
  if (!antwort.ok) {
    // Der Server schickt bei Formatfehlern eine verständliche deutsche Meldung.
    let meldung = `${was} konnte nicht gelesen werden.`;
    try {
      const koerper = await antwort.json();
      if (typeof koerper?.message === "string") meldung = koerper.message;
    } catch {
      /* keine JSON-Antwort — bei der Standardmeldung bleiben */
    }
    throw new DokumentScanFehler(meldung);
  }

  return (await antwort.json()) as ScanAntwort<T>;
};

export const leseVisitenkarte = (datei: File): Promise<ScanAntwort<VisitenkarteBefund>> =>
  leseDokument<VisitenkarteBefund>("visitenkarte", datei);

export const lesePersonalausweis = (datei: File): Promise<ScanAntwort<PersonalausweisBefund>> =>
  leseDokument<PersonalausweisBefund>("personalausweis", datei);

export const leseGehaltsabrechnung = (
  datei: File,
): Promise<ScanAntwort<GehaltsabrechnungBefund>> =>
  leseDokument<GehaltsabrechnungBefund>("gehaltsabrechnung", datei);

export const leseFuehrerschein = (datei: File): Promise<ScanAntwort<FuehrerscheinBefund>> =>
  leseDokument<FuehrerscheinBefund>("fuehrerschein", datei);

export const leseGeburtsurkunde = (datei: File): Promise<ScanAntwort<GeburtsurkundeBefund>> =>
  leseDokument<GeburtsurkundeBefund>("geburtsurkunde", datei);

export const leseHeiratsurkunde = (datei: File): Promise<ScanAntwort<HeiratsurkundeBefund>> =>
  leseDokument<HeiratsurkundeBefund>("heiratsurkunde", datei);

export const leseSterbeurkunde = (datei: File): Promise<ScanAntwort<SterbeurkundeBefund>> =>
  leseDokument<SterbeurkundeBefund>("sterbeurkunde", datei);

/** Liest eine .vcf-Datei als Text ein — die Datei verlässt den Browser nicht. */
export const leseVcardDatei = (datei: File): Promise<string> => datei.text();
